// Payment methods strip — branded chips with guaranteed WCAG-AA contrast
// (brand color as background, white text). Grouped into "Cards" and
// "Wallets & local methods" for clarity. Names double as textual content
// crawlers index; aria-labels give screen readers a clear description.

import { ShieldCheck, Lock } from "lucide-react";

type Method = { name: string; bg: string; fg?: string; label?: string };

const CARDS: Method[] = [
  { name: "VISA", bg: "#1A1F71" },
  { name: "Mastercard", bg: "#EB001B" },
  { name: "AMEX", bg: "#2E77BB", label: "American Express" },
  { name: "DISCOVER", bg: "#B34A00" },
  { name: "Diners", bg: "#0079BE", label: "Diners Club" },
  { name: "JCB", bg: "#0E4C96" },
  { name: "UnionPay", bg: "#D10429" },
  { name: "CB", bg: "#0B4EA2", label: "Cartes Bancaires" },
  { name: "elo", bg: "#000000" },
];

const WALLETS: Method[] = [
  { name: " Pay", bg: "#000000", label: "Apple Pay" },
  { name: "G Pay", bg: "#1A73E8", label: "Google Pay" },
  { name: "PayPal", bg: "#003087" },
  { name: "amazon pay", bg: "#232F3E", label: "Amazon Pay" },
  { name: "Klarna", bg: "#FFA8CD", fg: "#0B051D" },
  { name: "Revolut Pay", bg: "#0666EB" },
  { name: "Alipay", bg: "#0A5FCC" },
  { name: "WeChat Pay", bg: "#04702A" },
  { name: "SEPA", bg: "#10298E" },
  { name: "iDEAL", bg: "#B0004F" },
  { name: "Bancontact", bg: "#005498" },
  { name: "giropay", bg: "#A00259" },
  { name: "EPS", bg: "#8A003A" },
  { name: "P24", bg: "#8A0308", label: "Przelewy24" },
  { name: "BLIK", bg: "#000000" },
  { name: "Trustly", bg: "#0EE06E", fg: "#0B051D" },
  { name: "Multibanco", bg: "#B01A10" },
];

function AppleGlyph({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true" fill={color}>
      <path d="M16.365 12.6c-.02-2.05 1.68-3.03 1.76-3.08-.96-1.4-2.46-1.59-2.99-1.61-1.27-.13-2.48.75-3.13.75-.65 0-1.65-.73-2.72-.71-1.4.02-2.69.81-3.41 2.06-1.45 2.52-.37 6.24 1.05 8.29.69 1 1.52 2.13 2.6 2.09 1.04-.04 1.44-.67 2.7-.67 1.26 0 1.61.67 2.72.65 1.12-.02 1.83-1.02 2.52-2.02.79-1.15 1.12-2.27 1.13-2.33-.02-.01-2.17-.83-2.2-3.28z" />
    </svg>
  );
}

function Chip({ m }: { m: Method }) {
  const isApple = m.label === "Apple Pay";
  const fg = m.fg ?? "#FFFFFF";
  return (
    <li>
      <span
        role="img"
        aria-label={m.label ?? m.name}
        title={m.label ?? m.name}
        className="inline-flex h-7 min-w-[46px] items-center justify-center gap-1 rounded-md px-2.5 text-[11px] font-black tracking-tight shadow-sm ring-1 ring-black/10 transition-all hover:-translate-y-0.5 hover:shadow-md"
        style={{
          background: m.bg,
          color: fg,
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        }}
      >
        {isApple && <AppleGlyph color={fg} />}
        <span>{m.name}</span>
      </span>
    </li>
  );
}

function Group({ title, items }: { title: string; items: Method[] }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-paper/80">
        {title}
      </span>
      <ul className="flex flex-wrap items-center gap-1.5" aria-label={title}>
        {items.map((m) => (
          <Chip key={m.label ?? m.name} m={m} />
        ))}
      </ul>
    </div>
  );
}

export function PaymentMethods() {
  const all = [...CARDS, ...WALLETS];
  return (
    <section aria-labelledby="payment-methods-heading" className="border-t border-white/10">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h2
              id="payment-methods-heading"
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-sky-pop"
            >
              Secure payment methods
            </h2>
            <p className="mt-1 text-xs text-paper/85">
              Trusted checkout powered by Stripe — pay your way.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-paper/85">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3 text-sky-pop" aria-hidden="true" />
              Encrypted checkout
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-sky-pop" aria-hidden="true" />
              3-D Secure
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-sky-pop" aria-hidden="true" />
              PCI-DSS
            </span>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[auto_1fr] lg:gap-10">
          <Group title="Cards" items={CARDS} />
          <Group title="Wallets & local methods" items={WALLETS} />
        </div>
      </div>

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "FIXHEN",
            url: "https://fixhen.com",
            paymentAccepted: all.map((m) => m.label ?? m.name.trim()).join(", "),
            currenciesAccepted: "EUR",
          }),
        }}
      />
    </section>
  );
}
